import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { axiosSalary } from "../api/axiosInstance";
import { useAuth } from "../auth/AuthContext";

const AttendanceManagerByGroup = () => {

  const { user } = useAuth();
  const groupId = user?.groupId;

  const [restaurantList, setRestaurantList] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const [attendanceDate, setAttendanceDate] = useState("");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ================= DEFAULT DATE ================= */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setAttendanceDate(today);
  }, []);

  /* ================= FETCH RESTAURANTS ================= */
  const fetchRestaurants = async () => {
    try {
      const res = await axiosSalary.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );

      const options = (res.data || []).map(r => ({
        value: r.restId,
        label: r.restaurantName
      }));

      setRestaurantList(options);
      setSelectedRestaurant(options[0]);

    } catch (err) {
      console.error("Restaurant error:", err);
    }
  };

  useEffect(() => {
    if (groupId) fetchRestaurants();
  }, [groupId]);

  /* ================= FETCH REPORT (SINGLE DATE) ================= */
  const fetchReport = async () => {
    if (!selectedRestaurant?.value || !attendanceDate) return;

    try {
      setLoading(true);

      const res = await axiosSalary.get(
        `/DailyAttendanceReport/DailyReportByRestaurantAndDate?groupId=${groupId}&restId=${selectedRestaurant.value}&attendanceDate=${attendanceDate}`
      );

      setData(res.data || []);

    } catch (err) {
      console.error("Report error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [selectedRestaurant, attendanceDate]);

  /* ================= STATUS BADGE ================= */
  const getStatus = (row) => {
    if (row.isAbsent)
      return <span className="bg-gray-300 px-2 py-1 rounded text-xs">Absent</span>;
    if (row.isSuspense)
      return <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">Suspense</span>;
    if (row.isEarlyLeaving)
      return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">Early Leave</span>;
    if (row.isLate)
      return <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">Late</span>;

    return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Present</span>;
  };

  /* ================= SUMMARY ================= */
  const summary = useMemo(() => {
    let present = 0, absent = 0;

    data.forEach(d => {
      if (d.isPresent) present++;
      if (d.isAbsent) absent++;
    });

    return {
      total: data.length,
      present,
      absent
    };
  }, [data]);

  return (
    <div className="bg-white p-4 md:p-5 rounded-xl shadow border">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between gap-3 mb-3">

        <h2 className="text-lg font-semibold">
          Attendance Manager (Daily)
        </h2>

        <div className="flex flex-wrap gap-2 items-center ml-auto">

          {/* Restaurant */}
          <Select
            value={selectedRestaurant}
            onChange={setSelectedRestaurant}
            options={restaurantList}
            placeholder="Select Restaurant"
            className="text-sm min-w-[180px]"
          />

          {/* SINGLE DATE */}
          <input
            type="date"
            value={attendanceDate}
            onChange={(e) => setAttendanceDate(e.target.value)}
            className="border px-2 py-1 rounded text-sm"
          />

        </div>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-3 gap-2 text-xs md:text-sm mb-3">
        <div className="bg-gray-100 p-2 rounded text-center">
          <p>Total</p><p>{summary.total}</p>
        </div>

        <div className="bg-green-100 p-2 rounded text-center">
          <p>Present</p><p>{summary.present}</p>
        </div>

        <div className="bg-gray-300 p-2 rounded text-center">
          <p>Absent</p><p>{summary.absent}</p>
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="text-center py-3 text-blue-600 text-sm">
          Loading data...
        </div>
      )}

      {/* DESKTOP TABLE */}
      <div className="hidden md:block border rounded-lg max-h-[500px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="border p-2">ID</th>
              <th className="border p-2">Name</th>
              <th className="border p-2">Date</th>
              <th className="border p-2">Punch</th>
              <th className="border p-2">Work Min</th>
              <th className="border p-2">OT</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Remarks</th>
            </tr>
          </thead>

          <tbody>
            {data.map(row => (
              <tr key={row.empId + row.attendanceDate}>
                <td className="border p-2">{row.empId}</td>
                <td className="border p-2">{row.empName}</td>
                <td className="border p-2">{row.attendanceDateDisplay}</td>

                <td className="border p-2 text-center">
                  <span className={`px-2 py-1 rounded text-xs ${
                    row.totalPunches % 2 !== 0
                      ? "bg-red-100 text-red-700"
                      : "bg-green-100 text-green-700"
                  }`}>
                    {row.totalPunches}
                  </span>
                </td>

                <td className="border p-2">{row.workingMinutes}</td>
                <td className="border p-2 text-blue-600">{row.overtimeMinutes}</td>

                <td className="border p-2">{getStatus(row)}</td>
                <td className="border p-2 text-xs">{row.remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE VIEW */}
      <div className="md:hidden space-y-3 mt-3">
        {data.map(row => (
          <div key={row.empId + row.attendanceDate} className="border p-3 rounded-xl shadow-sm bg-white">

            <div className="flex justify-between items-center mb-1">
              <h3 className="font-semibold text-sm">{row.empName}</h3>
              {getStatus(row)}
            </div>

            <div className="text-xs text-gray-600">
              <div>ID: {row.empId}</div>
              <div>Date: {row.attendanceDateDisplay}</div>
            </div>

            <div className="mt-2">
              <span className={`px-2 py-1 rounded text-xs ${
                row.totalPunches % 2 !== 0
                  ? "bg-red-100 text-red-700"
                  : "bg-green-100 text-green-700"
              }`}>
                Punch: {row.totalPunches}
              </span>
            </div>

            <div className="mt-2 text-xs flex justify-between">
              <span>Work: {row.workingMinutes}</span>
              <span className="text-blue-600">OT: {row.overtimeMinutes}</span>
            </div>

            <div className="mt-2 text-xs text-gray-700">
              {row.remarks}
            </div>

          </div>
        ))}
      </div>

    </div>
  );
};

export default AttendanceManagerByGroup;